import React from 'react';
import {
  Alert as MuiAlert,
  Button as MuiButton,
  Chip as MuiChip,
  FormControlLabel as MuiFormControlLabel,
  InputLabel as MuiInputLabel,
  ListItemText as MuiListItemText,
  MenuItem as MuiMenuItem,
  Tab as MuiTab,
  TableCell as MuiTableCell,
  TextField as MuiTextField,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { translateInline } from '../../i18n/inlineTranslations';

const catalogCache = new Map<string, Map<string, string>>();
const getCatalog = (i18n: any, language: string) => {
  if (catalogCache.has(language)) return catalogCache.get(language)!;
  const catalog = new Map<string, string>();
  const visit = (source: any, target: any) => Object.entries(source || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && typeof target?.[key] === 'string') catalog.set(value, target[key]);
    else if (value && typeof value === 'object') visit(value, target?.[key]);
  });
  visit(i18n.getResourceBundle('en', 'translation'), i18n.getResourceBundle(language, 'translation'));
  catalogCache.set(language, catalog);
  return catalog;
};

const useLocalizer = () => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const catalog = getCatalog(i18n, language);
  const localizeString = React.useCallback((value: string) => {
    const leading = value.match(/^\s*/)?.[0] || '';
    const trailing = value.match(/\s*$/)?.[0] || '';
    const source = value.trim();
    if (!source) return value;
    const inline = translateInline(language, source);
    return `${leading}${inline === source ? catalog.get(source) || source : inline}${trailing}`;
  }, [catalog, language]);
  return React.useCallback((node: React.ReactNode): React.ReactNode => {
    if (typeof node === 'string') return localizeString(node);
    if (Array.isArray(node)) return node.map((item) => {
      if (typeof item !== 'string') return item;
      return localizeString(item);
    });
    return node;
  }, [localizeString]);
};

export const LocalizedAlert = React.forwardRef<any, any>(({ children, ...props }, ref) => { const tr = useLocalizer(); return <MuiAlert ref={ref} {...props}>{tr(children)}</MuiAlert>; }) as typeof MuiAlert;
export const LocalizedButton = React.forwardRef<any, any>(({ children, ...props }, ref) => { const tr = useLocalizer(); return <MuiButton ref={ref} {...props}>{tr(children)}</MuiButton>; }) as typeof MuiButton;
export const LocalizedMenuItem = React.forwardRef<any, any>(({ children, ...props }, ref) => { const tr = useLocalizer(); return <MuiMenuItem ref={ref} {...props}>{tr(children)}</MuiMenuItem>; }) as typeof MuiMenuItem;
export const LocalizedTableCell = React.forwardRef<any, any>(({ children, ...props }, ref) => { const tr = useLocalizer(); return <MuiTableCell ref={ref} {...props}>{tr(children)}</MuiTableCell>; }) as typeof MuiTableCell;
export const LocalizedInputLabel = React.forwardRef<any, any>(({ children, ...props }, ref) => { const tr = useLocalizer(); return <MuiInputLabel ref={ref} {...props}>{tr(children)}</MuiInputLabel>; }) as typeof MuiInputLabel;
export const LocalizedTooltip = React.forwardRef<any, any>(({ title, children, ...props }, ref) => { const tr = useLocalizer(); return <MuiTooltip ref={ref} title={tr(title)} {...props}>{children}</MuiTooltip>; }) as typeof MuiTooltip;
export const LocalizedTab = React.forwardRef<any, any>(({ label, ...props }, ref) => { const tr = useLocalizer(); return <MuiTab ref={ref} label={tr(label)} {...props} />; }) as typeof MuiTab;
export const LocalizedChip = React.forwardRef<any, any>(({ label, ...props }, ref) => { const tr = useLocalizer(); return <MuiChip ref={ref} label={tr(label)} {...props} />; }) as typeof MuiChip;
export const LocalizedTextField = React.forwardRef<any, any>(({ label, helperText, placeholder, ...props }, ref) => { const tr = useLocalizer(); return <MuiTextField ref={ref} label={tr(label)} helperText={tr(helperText)} placeholder={typeof placeholder === 'string' ? tr(placeholder) as string : placeholder} {...props} />; }) as typeof MuiTextField;
export const LocalizedFormControlLabel = React.forwardRef<any, any>(({ label, control, ...props }, ref) => { const tr = useLocalizer(); return <MuiFormControlLabel ref={ref} label={tr(label)} control={control} {...props} />; }) as typeof MuiFormControlLabel;
export const LocalizedListItemText = React.forwardRef<any, any>(({ primary, secondary, ...props }, ref) => { const tr = useLocalizer(); return <MuiListItemText ref={ref} primary={tr(primary)} secondary={tr(secondary)} {...props} />; }) as typeof MuiListItemText;
